/**
 * Fractional metre/kg stepper for cart LINES (cart drawer + any native
 * cart-products.liquid usage). Companion to fabric-quantity.js (product page)
 * and sections/tessuti-cart.liquid's inline .t-cart__qty--fq script (cart page).
 *
 * Loaded once globally (snippets/scripts.liquid), because the drawer is
 * present on every page and its rows get morphed/replaced on every cart
 * update. Direct listeners bound at load time would go stale after the first
 * morph, so everything here is delegated on `document` and looked up via
 * event.target.closest(...) at the moment the event fires.
 *
 * Loaded as a real ES module (type="module") with a static top-level import
 * - matching page-view-event.js and product-form.js - not a dynamic
 * import(). Shopify's asset build rewrites dynamic import('@shopify/events')
 * into a CommonJS `require(...)` call that doesn't exist in the browser;
 * static imports are left as native ESM and resolve through the theme's
 * import map correctly.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

(function () {
  if (window.__fqCartLineInit) return;
  window.__fqCartLineInit = true;

  function round(value, dp) {
    var m = Math.pow(10, dp);
    return Math.round(value * m) / m;
  }

  function decimalsFor(step) {
    var t = step.toFixed(10).replace(/0+$/, '');
    var i = t.indexOf('.');
    return i < 0 ? 0 : t.length - i - 1;
  }

  function parseQty(v) {
    return parseFloat(String(v == null ? '' : v).replace(',', '.').trim());
  }

  function setBusy(box, busy) {
    box.querySelectorAll('button').forEach(function (b) {
      b.disabled = busy;
    });
  }

  function showError(box, message) {
    var el = box.querySelector('[data-fq-cartline-error]');
    if (!el) return;
    el.textContent = message || '';
    if (message) el.removeAttribute('hidden');
    else el.setAttribute('hidden', '');
  }

  function updatePreview(box) {
    var input = box.querySelector('[data-fq-cartline-input]');
    var note = box.querySelector('[data-fq-cartline-note]');
    if (!input || !note) return;
    var baseCount = parseFloat(box.dataset.baseCount) || 0;
    var baseUnit = box.dataset.baseUnit || '';
    var v = parseQty(input.value);
    if (isNaN(v) || !(baseCount > 0) || !baseUnit) return;
    note.textContent = 'approx. ' + round(v * baseCount, 2) + ' ' + baseUnit;
  }

  function sectionIds() {
    return Array.prototype.map
      .call(document.querySelectorAll('cart-items-component'), function (el) {
        return el.dataset.sectionId;
      })
      .filter(Boolean);
  }

  // Liquid's `item.properties | json` can serialize as either a plain
  // object ({"Quantity":"0.5 m"}) or an array of [key, value] pairs
  // depending on how the properties drop was passed through. Normalize
  // both shapes so a stray "0" key never gets written back to the cart.
  function parseProps(raw) {
    var props = {};
    var data;
    try {
      data = JSON.parse(raw || '{}');
    } catch (e) {
      return props;
    }
    if (Array.isArray(data)) {
      data.forEach(function (pair) {
        if (Array.isArray(pair) && pair.length === 2) props[pair[0]] = pair[1];
      });
    } else if (data && typeof data === 'object') {
      props = data;
    }
    return props;
  }

  function apply(box, units) {
    var input = box.querySelector('[data-fq-cartline-input]');
    if (!input) return;

    var step = parseFloat(box.dataset.step) || 0.05;
    var minUnits = parseInt(box.dataset.minUnits, 10) || 1;
    var maxUnits = box.dataset.maxUnits ? parseInt(box.dataset.maxUnits, 10) : null;
    var currentUnits = parseInt(box.dataset.currentUnits, 10) || minUnits;
    var uom = box.dataset.uom || '';
    var line = parseInt(box.dataset.line, 10);
    var lineKey = box.dataset.key || '';
    var decimals = decimalsFor(step);

    if (!isFinite(units)) units = minUnits;
    units = Math.round(units);
    if (units < minUnits) units = minUnits;
    if (maxUnits != null && units > maxUnits) units = maxUnits;

    var fixed = (units * step).toFixed(decimals);

    if (units === currentUnits) {
      input.value = fixed;
      updatePreview(box);
      return;
    }

    var props = parseProps(box.dataset.props);
    props.Quantity = fixed + (uom ? ' ' + uom : '');

    showError(box, '');
    setBusy(box, true);

    var body = JSON.stringify({
      line: line,
      quantity: units,
      properties: props,
      sections: sectionIds().join(','),
      sections_url: window.location.pathname,
    });

    fetch(Theme.routes.cart_change_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: body,
    })
      .then(function (res) {
        return res.text().then(function (text) {
          var data;
          try {
            data = JSON.parse(text);
          } catch (e) {
            data = null;
          }
          return { ok: res.ok, data: data };
        });
      })
      .then(function (result) {
        var data = result.data;
        if (!result.ok || !data || data.errors) {
          throw new Error((data && (data.errors || data.description || data.message)) || 'Could not update the cart.');
        }

        box.dataset.currentUnits = String(units);
        box.dataset.props = JSON.stringify(props);
        input.value = fixed;
        updatePreview(box);
        setBusy(box, false);

        document.dispatchEvent(
          new CartLinesUpdateEvent({
            action: units > 0 ? 'update' : 'remove',
            context: 'cart',
            lines: [{ id: lineKey, quantity: units }],
            promise: Promise.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
              detail: {
                sections: data.sections,
                items: data.items,
                itemCount: data.item_count,
                source: 'fabric-quantity-cart-line',
                didError: false,
              },
            }),
          })
        );
      })
      .catch(function (err) {
        setBusy(box, false);
        input.value = (currentUnits * step).toFixed(decimals);
        updatePreview(box);
        showError(box, err && err.message ? err.message : 'Could not update the cart.');
      });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-fq-cartline-delta]');
    if (!btn) return;
    var box = btn.closest('[data-fq-cartline]');
    if (!box) return;
    var current = parseInt(box.dataset.currentUnits, 10) || 0;
    apply(box, current + parseInt(btn.dataset.fqCartlineDelta, 10));
  });

  document.addEventListener('input', function (e) {
    var input = e.target.closest && e.target.closest('[data-fq-cartline-input]');
    if (!input) return;
    var box = input.closest('[data-fq-cartline]');
    if (!box) return;
    updatePreview(box);
  });

  // blur does not bubble, so this listener must be registered for the
  // capture phase to still work via delegation from `document`.
  document.addEventListener(
    'blur',
    function (e) {
      var input = e.target.closest && e.target.closest('[data-fq-cartline-input]');
      if (!input) return;
      var box = input.closest('[data-fq-cartline]');
      if (!box) return;
      var step = parseFloat(box.dataset.step) || 0.05;
      var currentUnits = parseInt(box.dataset.currentUnits, 10) || 1;
      var v = parseQty(input.value);
      if (isNaN(v) || v <= 0) {
        apply(box, currentUnits);
        return;
      }
      // Always round a typed quantity UP to the next multiple of `step`
      // (2.22 -> 2.25, never 2.20) - never give the customer less than they
      // asked for. The epsilon absorbs float noise on an already-exact multiple.
      apply(box, Math.ceil(v / step - 1e-9));
    },
    true
  );

  document.addEventListener('keydown', function (e) {
    var input = e.target.closest && e.target.closest('[data-fq-cartline-input]');
    if (!input) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      input.blur();
    }
  });
})();
